// Universal attachment governance — shared client helpers.
//
// These are convenience checks that give the user fast feedback; the real
// enforcement is server-side (storage.objects INSERT policy + bucket
// file_size_limit + manna_register_attachment), see migration
// 20260605000003_attachment_governance.sql. Keep the blocklist below in sync
// with platform.attachment_ext_is_blocked().

export const MAX_ATTACHMENT_BYTES = 100 * 1024 * 1024; // 100 MB

// Dangerous executable / script extensions. Mirror of the SQL predicate.
export const BLOCKED_EXTENSIONS: ReadonlySet<string> = new Set([
  // Windows executables / libraries
  "exe",
  "msi",
  "msp",
  "mst",
  "com",
  "scr",
  "pif",
  "cpl",
  "dll",
  "sys",
  "drv",
  "vxd",
  // Windows / shell scripts
  "bat",
  "cmd",
  "ps1",
  "psm1",
  "ps1xml",
  "psc1",
  "vbs",
  "vbe",
  "js",
  "jse",
  "wsf",
  "wsh",
  "wsc",
  "ws",
  "hta",
  // Unix shell scripts
  "sh",
  "bash",
  "zsh",
  "csh",
  "ksh",
  // Installers / packages / mobile apps
  "jar",
  "app",
  "deb",
  "rpm",
  "dmg",
  "pkg",
  "apk",
  // System / shortcut / registry
  "reg",
  "scf",
  "inf",
  "lnk",
  "gadget",
  "msc",
  // Misc auto-exec
  "ade",
  "adp",
  "application",
  "jnlp",
]);

export function extensionOf(filename: string): string {
  const parts = filename.toLowerCase().split(".");
  return parts.length > 1 ? (parts.pop() ?? "") : "";
}

export function isBlockedFile(filename: string): boolean {
  return BLOCKED_EXTENSIONS.has(extensionOf(filename));
}

export function formatMaxSize(): string {
  return "100 MB";
}

/**
 * Validate a file before upload. Returns an error message, or null if OK.
 */
export function validateAttachment(file: File): string | null {
  if (isBlockedFile(file.name)) {
    return `${file.name}: this file type is not allowed for security reasons.`;
  }
  if (file.size > MAX_ATTACHMENT_BYTES) {
    return `${file.name} exceeds the ${formatMaxSize()} limit.`;
  }
  return null;
}

// MIME types / extensions browsers render inline. When a file matches, the
// default click action opens a preview in a new tab; otherwise it downloads.
// SVG and HTML are intentionally excluded (they can carry scripts) — those
// download instead of previewing.
const PREVIEWABLE_MIME_PREFIXES = ["image/", "audio/", "video/"];
const PREVIEWABLE_MIME_EXACT = new Set([
  "application/pdf",
  "text/plain",
  "text/csv",
  "text/markdown",
]);
const PREVIEWABLE_EXTENSIONS = new Set([
  "pdf",
  "png",
  "jpg",
  "jpeg",
  "gif",
  "webp",
  "bmp",
  "txt",
  "csv",
  "md",
  "mp3",
  "wav",
  "ogg",
  "mp4",
  "webm",
  "mov",
]);

export function canPreviewInBrowser(mimeType: string | null, filename: string): boolean {
  const mime = (mimeType ?? "").toLowerCase();
  if (mime === "image/svg+xml" || mime === "text/html") return false;
  if (PREVIEWABLE_MIME_EXACT.has(mime)) return true;
  if (PREVIEWABLE_MIME_PREFIXES.some((p) => mime.startsWith(p))) return true;
  // Fall back to extension when the stored mime_type is missing/generic.
  if (!mime || mime === "application/octet-stream") {
    return PREVIEWABLE_EXTENSIONS.has(extensionOf(filename));
  }
  return false;
}
